import { useState, useEffect, useRef, useMemo } from 'react';
import BusinessSettingsForm from '../components/BusinessSettingsForm';
import BusinessHoursForm from '../components/BusinessHoursForm';
import BusinessLogoUpload from '../components/BusinessLogoUpload';
import { getTaxRates, TaxRate, deleteTaxRate } from '../lib/tax-rates';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import TaxRateModal from '../components/TaxRateModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { Plus, Pencil, Trash2, Smartphone, Check, Bell, ChevronDown, X, Building2, Package, DollarSign, Wrench, CalendarDays, Calendar, Users, FileText, MessageSquare, Megaphone, Receipt, ShoppingCart, Zap, ListChecks, BarChart3, Folder, LayoutDashboard } from 'lucide-react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import PermissionGuard from '../components/PermissionGuard';
import {
  CONTROL_CENTRE_APP_ID,
  isControlCentreApp,
  mergeHomeScreenAppOptions,
  setBusinessHomeScreen,
} from '../lib/controlCentreHome';

export default function BusinessSettings() {
  const { selectedBusiness } = useBusiness();
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [installedApps, setInstalledApps] = useState<any[]>([]);
  const [homeScreenApp, setHomeScreenApp] = useState<string | null>(null);
  const [loadingApps, setLoadingApps] = useState(true);
  const [isHomeScreenDropdownOpen, setIsHomeScreenDropdownOpen] = useState(false);
  const [homeScreenDropdownPosition, setHomeScreenDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const homeScreenButtonRef = useRef<HTMLButtonElement>(null);
  const [isTaxRateModalOpen, setIsTaxRateModalOpen] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | null>(null);
  const [taxRateModalMode, setTaxRateModalMode] = useState<'create' | 'edit'>('create');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taxRateToDelete, setTaxRateToDelete] = useState<TaxRate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadTaxRates = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getTaxRates(selectedBusiness.id);
      setTaxRates(data);
      setError(null);
    } catch (err) {
      console.error('Error loading tax rates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tax rates');
    } finally {
      setLoading(false);
    }
  };

  const loadInstalledApps = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoadingApps(true);
      const { data, error } = await supabase
        .from('business_apps_v2')
        .select(`
          id,
          app_id,
          is_home_screen,
          app:app_registry_v2(
            id,
            name,
            slug,
            icon,
            route_prefix
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .or('status.eq.active,is_home_screen.eq.true')
        .order('visible_app_grid', { ascending: true });

      if (error) {
        console.error('Error loading installed apps:', error);
        return;
      }

      const rows = data || [];
      const homeApp = rows.find((app) => app.is_home_screen);
      setHomeScreenApp(homeApp?.app_id || null);
      setInstalledApps(mergeHomeScreenAppOptions(rows));
    } catch (err) {
      console.error('Error in loadInstalledApps:', err);
    } finally {
      setLoadingApps(false);
    }
  };

  useEffect(() => {
    loadTaxRates();
    loadInstalledApps();
  }, [selectedBusiness?.id]);

  // Handle click outside dropdown
  useEffect(() => {
    if (!isHomeScreenDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (homeScreenButtonRef.current && homeScreenButtonRef.current.contains(target)) {
        return;
      }
      
      const dropdown = document.querySelector('[data-home-screen-dropdown]');
      if (dropdown && dropdown.contains(target)) {
        return;
      }
      
      setIsHomeScreenDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHomeScreenDropdownOpen]);

  // Update dropdown position
  useEffect(() => {
    if (!isHomeScreenDropdownOpen || !homeScreenButtonRef.current) return;

    const updatePosition = () => {
      if (homeScreenButtonRef.current) {
        const rect = homeScreenButtonRef.current.getBoundingClientRect();
        setHomeScreenDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isHomeScreenDropdownOpen]);

  // Helper function to get app display name
  const getAppDisplayName = (app: any) => {
    if (!app) return '';
    
    const slug = app.slug || app.id || '';
    const name = app.name || '';
    
    // Map slugs/IDs to display names
    if (slug === 'mobile-calendar' || slug === 'm-calendar' || name === 'Mobile Calendar' || name === 'Mobile-Pro' || name === 'm-calendar') {
      return 'Field-Pro';
    }
    if (slug === 'automations' || name === 'Automations') {
      return 'Automate';
    }
    if (slug === 'products' || name === 'Products') {
      return 'Store';
    }
    if (isControlCentreApp(app)) {
      return 'Control Centre';
    }

    return name;
  };

  const customAppIcons: { [key: string]: string } = {
    'analytics': '/Analyticsappicon.png',
    'projects': '/Projectsappicon.png',
    'calendar': '/Calenderappicon.png',
    'crm': '/CRMappicon.png',
    'proposals': '/Proposalsappicon.png',
    'tasks': '/Bookingsappicon.png',
    'inventory': '/Inventoryappicon.png',
    'expenses': '/Expensesappicon.png',
    'payroll': '/Timesheetsappicon.png',
    'teams': '/Teamsappicon.png',
    'invoices': '/Invoicesappicon.png',
    'automations': '/Automateappicon.png',
    'services': '/Servicesappicon.png',
    'customers': '/Customersappicon.png',
    'marketing': '/Marketingappicon.png',
    'gift-cards': '/Giftcardsappicon.png',
    'messages': '/Messagesappicon.png',
    'bookings': '/Bookingsappicon.png',
    'products': '/Storeappicon.png',
    'mobile-calendar': '/Fieldproappicon.png',
    'm-calendar': '/Fieldproappicon.png',
  };

  const getAppIcon = (appSlug: string, size: string = "h-5 w-5") => {
    const iconMap: { [key: string]: JSX.Element } = {
      'calendar': <Calendar className={size} />,
      'mobile-calendar': <Smartphone className={size} />,
      'm-calendar': <Smartphone className={size} />,
      'customers': <Users className={size} />,
      'automations': <Zap className={size} />,
      'teams': <Users className={size} />,
      'tasks': <ListChecks className={size} />,
      'analytics': <BarChart3 className={size} />,
      'crm': <Building2 className={size} />,
      'inventory': <Package className={size} />,
      'payroll': <DollarSign className={size} />,
      'services': <Wrench className={size} />,
      'bookings': <CalendarDays className={size} />,
      'proposals': <FileText className={size} />,
      'marketing': <Megaphone className={size} />,
      'invoices': <Receipt className={size} />,
      'messages': <MessageSquare className={size} />,
      'products': <ShoppingCart className={size} />,
      'projects': <Folder className={size} />,
      'control-centre': <LayoutDashboard className={size} />,
    };

    return iconMap[appSlug] || <Package className={size} />;
  };

  const renderAppTile = (appSlug: string, size: string = "w-10 h-10") => {
    if (customAppIcons[appSlug]) {
      return (
        <div className={`${size} rounded-xl overflow-hidden bg-white shadow-md transition-all duration-200`}>
          <img src={customAppIcons[appSlug]} alt={appSlug} className="w-full h-full object-cover" draggable={false} style={{ imageRendering: '-webkit-optimize-contrast' as any }} />
        </div>
      );
    }
    return (
      <div className={`${size} bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center text-white shadow-md transition-all duration-200`}>
        {getAppIcon(appSlug, "h-5 w-5")}
      </div>
    );
  };

  const homeScreenOptions = useMemo(
    () => mergeHomeScreenAppOptions(installedApps),
    [installedApps]
  );

  const handleSetHomeScreen = async (appId: string) => {
    if (!selectedBusiness?.id) return;

    try {
      const result = await setBusinessHomeScreen(selectedBusiness.id, appId);
      if (!result.ok) {
        console.error('Error setting home screen:', result.error);
        toast.error(result.error || 'Failed to update home screen');
        return;
      }

      setHomeScreenApp(appId);
      setIsHomeScreenDropdownOpen(false);
      localStorage.removeItem('homeScreenRoute');
      await loadInstalledApps();
      toast.success('Home screen updated');
    } catch (err) {
      console.error('Error in handleSetHomeScreen:', err);
      toast.error('Failed to update home screen');
    }
  };

  const handleEditTaxRate = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setTaxRateModalMode('edit');
    setIsTaxRateModalOpen(true);
  };

  const handleAddTaxRate = () => {
    setSelectedTaxRate(null);
    setTaxRateModalMode('create');
    setIsTaxRateModalOpen(true);
  };

  const handleDeleteTaxRate = (taxRate: TaxRate) => {
    setTaxRateToDelete(taxRate);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteTaxRate = async () => {
    if (!taxRateToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteTaxRate(taxRateToDelete.id);
      await loadTaxRates();
      setIsDeleteModalOpen(false);
      setTaxRateToDelete(null);
    } catch (err) {
      console.error('Error deleting tax rate:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete tax rate');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <PermissionGuard permission="settings.pageAccess">
      <div 
        className="min-h-screen bg-gray-50" 
        data-business-settings-page
        style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'}}
      >
        {/* Content */}
        <div className="px-4 sm:px-4 py-4">
          {/* Desktop Grid Layout */}
          <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0 sm:items-start">
            {/* Home Screen Section - Row 1, Col 1 */}
            <div className="space-y-2">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4">
                Home Screen
              </h2>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 sm:p-5">
                  {loadingApps ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : homeScreenOptions.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No apps installed</p>
                  ) : (
                    <div className="space-y-3">
                      {/* Current Home Screen Display */}
                      {(() => {
                        const currentApp = homeScreenOptions.find((app) => app.app_id === homeScreenApp);
                        return currentApp ? (
                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {renderAppTile(currentApp.app.slug, "w-10 h-10")}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate">
                                  {getAppDisplayName(currentApp.app)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Current home screen
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 py-2">No home screen set</div>
                        );
                      })()}
                      
                      {/* Change Button */}
                      <button
                        ref={homeScreenButtonRef}
                        onClick={() => {
                          if (homeScreenButtonRef.current) {
                            const rect = homeScreenButtonRef.current.getBoundingClientRect();
                            setHomeScreenDropdownPosition({
                              top: rect.bottom + 8,
                              left: rect.left,
                              width: rect.width
                            });
                          }
                          setIsHomeScreenDropdownOpen(!isHomeScreenDropdownOpen);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <span>Change</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${isHomeScreenDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Business Information Section - Row 1-2, Col 2 (spans 2 rows) */}
            <div className="space-y-2 sm:row-span-2 sm:row-start-1">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4">
                Business Information
              </h2>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 sm:p-5 space-y-6">
                  <BusinessSettingsForm />
                  
                  {/* Tax Rates and Business Logo Row */}
                  <div className="pt-4 border-t border-gray-200/60">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {/* Tax Rates Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-xs font-semibold text-gray-900">Tax Rates</h3>
                          <button
                            onClick={handleAddTaxRate}
                            className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            Add Tax Rate
                          </button>
                        </div>
                        <div className="divide-y divide-gray-200/60">
                          {loading ? (
                            <div className="py-4 text-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                              <p className="mt-2 text-xs text-gray-600">Loading tax rates...</p>
                            </div>
                          ) : error ? (
                            <div className="py-3">
                              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-xs">
                                {error}
                              </div>
                            </div>
                          ) : taxRates.length === 0 ? (
                            <div className="py-4 text-center">
                              <p className="text-xs text-gray-500">No tax rates configured</p>
                              <button
                                onClick={handleAddTaxRate}
                                className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                              >
                                Add your first tax rate
                              </button>
                            </div>
                          ) : (
                            taxRates.map((taxRate) => (
                              <div key={taxRate.id} className="py-2.5 hover:bg-gray-50 transition-colors">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <h4 className="text-xs font-semibold text-gray-900">{taxRate.name}</h4>
                                      {taxRate.is_default && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">
                                          Default
                                        </span>
                                      )}
                                      {taxRate.is_compound && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700">
                                          Compound
                                        </span>
                                      )}
                                    </div>
                                    {taxRate.description && (
                                      <p className="mt-0.5 text-xs text-gray-600">{taxRate.description}</p>
                                    )}
                                    <div className="mt-1 text-xs font-semibold text-gray-900">
                                      Rate: {taxRate.rate}%
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => handleEditTaxRate(taxRate)}
                                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTaxRate(taxRate)}
                                      className="p-1.5 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Business Logo Upload Section */}
                      <div>
                        <BusinessLogoUpload size="medium" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours Section - Row 2, Col 1 */}
            <div className="space-y-2">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4">
                Business Hours
              </h2>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 sm:p-5">
                  <BusinessHoursForm />
                </div>
              </div>
            </div>
          </div>

          {/* Home Screen Dropdown */}
          {isHomeScreenDropdownOpen && homeScreenDropdownPosition && homeScreenOptions.length > 0 && createPortal(
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-[9998]"
                onClick={() => setIsHomeScreenDropdownOpen(false)}
              />
              
              {/* Dropdown Menu */}
              <div
                data-home-screen-dropdown
                className="fixed bg-white rounded-xl shadow-xl border border-gray-200 z-[9999] overflow-hidden max-h-[60vh] overflow-y-auto"
                style={{
                  top: `${homeScreenDropdownPosition.top}px`,
                  left: `${homeScreenDropdownPosition.left}px`,
                  width: `${Math.max(homeScreenDropdownPosition.width, 360)}px`,
                  maxWidth: '90vw',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4">
                  <div className="grid grid-cols-4 gap-4">
                    {homeScreenOptions.map((appData) => (
                      <button
                        key={appData.id}
                        onClick={() => handleSetHomeScreen(appData.app_id)}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all touch-manipulation relative ${
                          homeScreenApp === appData.app_id
                            ? 'bg-blue-50 ring-2 ring-blue-500'
                            : 'hover:bg-gray-50 active:bg-gray-100'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {renderAppTile(appData.app.slug, "w-12 h-12")}
                        <span className="text-xs font-medium text-gray-900 text-center leading-tight line-clamp-2">
                          {getAppDisplayName(appData.app)}
                        </span>
                        {homeScreenApp === appData.app_id && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}


        </div>
      </div>

      <TaxRateModal
        isOpen={isTaxRateModalOpen}
        onClose={() => {
          setIsTaxRateModalOpen(false);
          setSelectedTaxRate(null);
        }}
        onSuccess={() => {
          setIsTaxRateModalOpen(false);
          setSelectedTaxRate(null);
          loadTaxRates();
        }}
        businessId={selectedBusiness?.id}
        taxRate={selectedTaxRate}
        mode={taxRateModalMode}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTaxRateToDelete(null);
        }}
        onConfirm={confirmDeleteTaxRate}
        title="Delete Tax Rate"
        message={`Are you sure you want to delete the "${taxRateToDelete?.name}" tax rate? This action cannot be undone.`}
        loading={deleteLoading}
      />

    </PermissionGuard>
  );
}
